// client/src/api/routes/routesApi.ts
//
// Dominio: Routes
// Lift-and-shift FEDELE da services/apiClient.ts — nessuna logica riscritta,
// nessun contratto API modificato. Le firme e i return type ricalcano 1:1
// l'originale per garantire retrocompatibilità totale con la UI esistente.
//
// Dipende da: @/api/core/boundApiFetch (wrapper con store iniettato)

import { boundApiFetch } from "@/api/core/boundApiFetch";
import type { Route } from "@/types";
import {
  ChunkedRouteInputSchema,
  ChunkedRouteUploadResponseSchema,
  type ChunkedRouteInput,
  type ChunkedRouteUploadResponse,
} from "rideconnect-shared";

// ── Tipi locali ───────────────────────────────────────────────────────────────

export interface RouteSharePayload {
  to_user_id?: string;
  to_email?: string;
}

export interface RouteShare {
  id: string;
  route_id: string;
  to_user_id?: string | null;
  to_email?: string | null;
  status?: string;
  created_at?: string;
  [k: string]: unknown;
}

// ── Funzioni ──────────────────────────────────────────────────────────────────

/**
 * Le API dei percorsi restituiscono il payload dentro una chiave contenitore
 * (`{ route }`, `{ share }`, `{ shares }`, `{ routes }`) oppure inline.
 * `unwrap` riproduce esattamente il vecchio `data?.x ?? data`.
 */
function unwrap<T>(data: unknown, key: string): T {
  if (data && typeof data === "object" && key in data) {
    const inner = (data as Record<string, unknown>)[key];
    if (inner != null) return inner as T;
  }
  return data as T;
}

export async function getRoutes() {
  return boundApiFetch<{ routes: Route[] }>("/api/routes");
}

export async function getRouteById(id: string): Promise<Route> {
  const data = await boundApiFetch<unknown>(`/api/routes/${id}`);
  return unwrap<Route>(data, "route");
}

export async function createRoute(route: Partial<Route>) {
  return boundApiFetch("/api/routes", {
    method: "POST",
    body: JSON.stringify(route),
  }) as Promise<{ route: Route }>;
}

/**
 * POST /api/routes/chunked — Upload percorso con waypoints compressi.
 *
 * FASE 1 FIX (Agosto 2026): valida il body con la shared Zod schema
 * (union array | stringa base64) PRIMA dell'invio (pre-flight) e valida
 * la risposta (contract check). "Expected array, received string" non
 * può più verificarsi: il contratto è condiviso con il server.
 */
export async function createRouteChunked(
  input: ChunkedRouteInput,
): Promise<ChunkedRouteUploadResponse> {
  const preflight = ChunkedRouteInputSchema.safeParse(input);
  if (!preflight.success) {
    const first = preflight.error.issues[0];
    throw new Error(
      `Payload percorso non valido (${first?.path?.join(".") || "body"}): ${first?.message || "errore di validazione"}`,
    );
  }
  const data = (await boundApiFetch("/api/routes/chunked", {
    method: "POST",
    body: JSON.stringify(preflight.data),
  })) as unknown;
  const parsed = ChunkedRouteUploadResponseSchema.safeParse(data);
  if (!parsed.success) {
    throw new Error(
      `Risposta server non conforme al contratto: ${parsed.error.issues[0]?.message || "errore"}`,
    );
  }
  return parsed.data;
}

export async function patchRoute(
  id: string,
  patch: Partial<Route>,
): Promise<Route> {
  const data = await boundApiFetch<unknown>(`/api/routes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  return unwrap<Route>(data, "route");
}

/**
 * FEAT (Ott 2026) — Clona un percorso salvato (premium/trial).
 * Il server crea una copia PRIVATA con il nuovo nome; i waypoint sono
 * copiati identici (l'eventuale modifica avviene poi nell'editor).
 */
export async function duplicateRoute(
  id: string,
  name: string,
): Promise<Route> {
  const data = await boundApiFetch<unknown>(`/api/routes/${id}/duplicate`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return unwrap<Route>(data, "route");
}

/**
 * FEAT (Ott 2026) — Importa un percorso da file (KML/GeoJSON/GPX) — premium.
 * I waypoint sono già parsati dal client; il server valida e crea il percorso.
 */
export async function importRoute(input: {
  name: string;
  waypoints: Array<{ lat: number; lng: number; ts?: number | null }>;
  import_format?: "kml" | "geojson" | "gpx";
}): Promise<Route> {
  const data = await boundApiFetch<unknown>(`/api/routes/import`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return unwrap<Route>(data, "route");
}

export async function deleteRoute(id: string) {
  return boundApiFetch<{ ok: boolean }>(`/api/routes/${id}`, {
    method: "DELETE",
  });
}

export async function shareRoute(
  routeId: string,
  payload: RouteSharePayload,
): Promise<RouteShare> {
  const data = await boundApiFetch<unknown>(`/api/routes/${routeId}/share`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return unwrap<RouteShare>(data, "share");
}

export async function getRouteSharesOutbox(
  routeId?: string,
): Promise<RouteShare[]> {
  const qs = routeId ? `?route_id=${encodeURIComponent(routeId)}` : "";
  const data = await boundApiFetch<unknown>(
    `/api/routes/shares/outbox${qs}`,
  );
  return unwrap<RouteShare[]>(data, "shares");
}

/**
 * FEAT ROUTE-FILTERS (Set 2026): percorsi condivisi "insieme" tra me e un
 * altro biker (entrambe le direzioni, pending/accepted).
 */
export async function getRoutesSharedWith(
  otherUserId: string,
): Promise<Route[]> {
  const data = await boundApiFetch<unknown>(
    `/api/routes/shared-with/${encodeURIComponent(otherUserId)}`,
  );
  return unwrap<Route[]>(data, "routes");
}

export async function acceptRouteShare(shareId: string): Promise<RouteShare> {
  const data = await boundApiFetch<unknown>(
    `/api/routes/shares/${shareId}/accept`,
    { method: "POST" },
  );
  return unwrap<RouteShare>(data, "share");
}

export async function declineRouteShare(shareId: string): Promise<RouteShare> {
  const data = await boundApiFetch<unknown>(
    `/api/routes/shares/${shareId}/decline`,
    { method: "POST" },
  );
  return unwrap<RouteShare>(data, "share");
}
